import { Validation, VariantAttribute } from '@vp/upload-and-print-validator';
import { ProductInformation } from 'src/providers/ProductContextProvider';
import { retrieveFileRequirements } from 'src/services/UploadAndPrintService';
import { noticeError } from 'src/utils/newRelicUtil';
import { UN_EMBEDDED_FONTS_PAGE_LIMIT } from './Constants';
import { getTranslatedNameForProductOptionValue } from './productOptionUtils';

export const getValidationsForProduct = async (product: ProductInformation): Promise<Validation[]> => {
  try {
    return await retrieveFileRequirements(product.productKey, product.productVersion, product.selectedOptions);
  } catch (e) {
    noticeError(e, {
      method: 'getValidationsForProduct',
      message: 'Error retrieving file requirements for product',
    });
    return [];
  }
};

export const hasValidationErrors = (validations: Validation[] | null): boolean => {
  return validations !== null && validations.filter(v => v.state === 'Error').length > 0;
};

export const processValidations = (
  validations: Validation[],
  productOptionTranslations: VariantAttribute[] | null
): Validation[] => {
  const sortedValidations =
    validations?.sort((a, b) => {
      const rankA = a.rank || 0;
      const rankB = b.rank || 0;
      return rankA - rankB;
    }) ?? null;

  sortedValidations?.forEach(validation => {
    if (validation.productOptionKey && validation.tokenValues?.attributeValue) {
      const translatedValue = getTranslatedNameForProductOptionValue(
        productOptionTranslations,
        validation.productOptionKey,
        validation.tokenValues.attributeValue
      );

      if (translatedValue) {
        validation.tokenValues.attributeValue = translatedValue;
      }
    }
  });

  return sortedValidations;
};

export function getUnEmbeddedFilePages(unEmbeddedPages: number[]) {
  return unEmbeddedPages.slice(0, UN_EMBEDDED_FONTS_PAGE_LIMIT).join(', ');
}

// This function returns an array of page numbers that contain un-embedded fonts in the user-uploaded PDF.
export function getUnEmbeddedFontPages(unEmbeddedFonts: {
  pages: Record<string, { UnEmbeddedFontsCount: number }>;
}): number[] {
  return Object.keys(unEmbeddedFonts.pages).reduce<number[]>((acc, page) => {
    if (unEmbeddedFonts.pages[page].UnEmbeddedFontsCount !== 0) {
      acc.push(parseInt(page) + 1);
    }
    return acc;
  }, []);
}

/** `File` objects are immutable, that is why we cannot directly change their name.
 * So we are creating a new `File` instance with the same content and metadata, but with the new name.
 */
export function renameFile(file: File, newName: string): File {
  return new File([file], newName, { type: file.type, lastModified: file.lastModified });
}
